import { Button } from '@askable/ui/core/button';
import { toast } from '@askable/ui/core/sonner';

import i18n from 'src/i18n';

const { t } = i18n;

const PAUSED_TOAST_ID = 'live-updates-paused';
const STOPPED_TOAST_ID = 'live-updates-stopped';

// The Toaster's error style puts the button on red. Losing live updates isn't the user's error,
// so this takes the success toast's neutral surface, with a red dot to mark it.
export const LiveUpdatesStoppedToast = () => (
  <div className="flex w-[360px] items-center gap-2 rounded-lg border border-border bg-popover p-3 text-popover-foreground drop-shadow-lg">
    <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-destructive" />
    <p className="flex-1 text-sm">{t('global.liveUpdates.stopped')}</p>
    <Button variant="primary" onClick={() => window.location.reload()}>
      {t('global.refresh')}
    </Button>
  </div>
);

// Long enough that an outage the reconnect backoff recovers from never reaches the user.
const SHOW_PAUSED_AFTER_VISIBLE_MS = 60_000;

/** Calls `callback` once the page has been visible for `ms` in total. Returns a cancel function. */
const afterVisibleTime = (ms: number, callback: () => void) => {
  let remaining = ms;
  let startedAt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const pause = () => {
    clearTimeout(timer);
    timer = undefined;
    remaining -= Date.now() - startedAt;
  };
  const resume = () => {
    startedAt = Date.now();
    timer = setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      callback();
    }, remaining);
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && !timer) resume();
    else if (document.visibilityState === 'hidden' && timer) pause();
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  if (document.visibilityState === 'visible') resume();

  return () => {
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};

export const createLiveUpdatesNotice = () => {
  let cancelPaused: (() => void) | undefined;

  const clearPaused = () => {
    cancelPaused?.();
    cancelPaused = undefined;
    toast.dismiss(PAUSED_TOAST_ID);
  };

  return {
    reconnecting: () => {
      cancelPaused ??= afterVisibleTime(SHOW_PAUSED_AFTER_VISIBLE_MS, () =>
        toast.warning(t('global.liveUpdates.paused'), { id: PAUSED_TOAST_ID, duration: Infinity }),
      );
    },
    stopped: () => {
      clearPaused();
      toast.custom(() => <LiveUpdatesStoppedToast />, { id: STOPPED_TOAST_ID, duration: Infinity });
    },
    clearPaused,
  };
};
